import React, { useEffect } from 'react';
import { routes } from '../../config';
import { setIdpParam } from '../../utils/authprovider';
import { setClinicSynced } from '../../utils/mcpClinicSync';

/**
 * Hidden MCP iframe that switches MCP's active clinic
 * (CHANGE_ACTIVE_CLINIC_DPX) to the given clinic and records the sync once MCP
 * confirms it. This lets MCP-iframe pages (e.g. Clinic Equipment, service
 * request flows) establish the active clinic themselves instead of depending
 * on the home-page Programming File Requests pod being loaded first
 *
 * @param {object} props
 * @param {string|number} props.cochlearId - The active clinic's cochlearId.
 * @param {() => void} [props.onSynced] - Called once MCP confirms the switch.
 */
const ClinicSyncFrame = ({ cochlearId, onSynced }) => {
  useEffect(() => {
    if (!cochlearId) return undefined;

    const validSyncTargets = [
      'programmingFileList',
      'onlineServicesDashboard',
    ];
    const providerErrorTargets = [
      'dashboard',
      'onlineServicesDashboard',
    ];

    const handleMessage = (e) => {
      if (e.origin !== routes.mcp.domain) return;
      if (typeof e.data !== 'object' || !('pageEvent' in e.data)) {
        return;
      }

      const { type, target } = e.data.pageEvent;

      // If MCP cannot switch to the requested clinic (for example when the
      // clinic is not present in MCP), it can emit providerError instead of a
      // successful clinic-sync pageLoaded target.
      // Proceed so the parent page can render and surface the provider-level
      // error message rather than getting stuck in sync.
      if (
        type === 'providerError' &&
        providerErrorTargets.includes(target)
      ) {
        if (onSynced) onSynced();
        return;
      }

      if (
        type === 'pageLoaded' &&
        validSyncTargets.includes(target)
      ) {
        setClinicSynced(cochlearId);
        if (onSynced) onSynced();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [cochlearId, onSynced]);

  if (!cochlearId) return null;

  return (
    <iframe
      src={setIdpParam(
        `${routes.mcp.programmingFileRequests}${cochlearId}`,
      )}
      title="clinic-sync"
      aria-hidden="true"
      style={{ display: 'none' }}
    />
  );
};

export default ClinicSyncFrame;
